import clsx from "clsx";

import { Autocomplete as UIKitAutocomplete } from "@proda-ai/ui-kit";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import React from "react";

export const CORRECT_ICON_CLASS = "fa-angle-down";
export const INCORRECT_ICON_CLASS = "fa-caret-down";

const replaceCaretDownIconWithAngleDownAsThisPropCannotBeOverriddenInUIKit = (
    containerRef: React.RefObject<HTMLDivElement>,
) => {
    if (!containerRef.current) return;

    const icon = containerRef.current.querySelector(
        ".uikit-text-field__trailing-icon",
    );
    if (icon) {
        icon.classList.remove(INCORRECT_ICON_CLASS);
        icon.classList.add(CORRECT_ICON_CLASS);
    }
};

const alignInputBorderStyles = {
    "& input": {
        border: "1px solid var(--proda-white-150) !important",
    },
};

const alignDisabledInputStyles = (disabled: boolean) => ({
    "& input:read-only": {
        backgroundColor: disabled ? "var(--proda-white-130)" : "white",
        cursor: disabled ? "not-allowed" : "pointer",
    },
    ...(disabled
        ? {
              "& .uikit-text-field__trailing-icon": {
                  display: "none",
              },
          }
        : {}),
});

export type TAutocompleteOption<T> = {
    value: T;
    label: string;
};

export type AutocompleteProps<T> = JSX.IntrinsicAttributes & {
    options: TAutocompleteOption<T>[];
    value: T | null;
    onChange: (value: T) => void;
    placeholder?: string;
    label?: string;
    hideLabel?: boolean;
    disabled?: boolean;
    className?: string;
    id?: string;
    sx?: BoxProps["sx"];
};

const testId = "Autocomplete";
const Autocomplete = <T extends string | number>(
    props: AutocompleteProps<T>,
) => {
    const {
        options,
        value,
        onChange,
        placeholder = "Select an option",
        label = "Select an option",
        hideLabel = false,
        disabled = false,
        className,
        id = "controlled-dropdown",
        sx,
    } = props;

    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        replaceCaretDownIconWithAngleDownAsThisPropCannotBeOverriddenInUIKit(
            containerRef,
        );
    }, []);

    const optionsAsAutocompleteOptions = React.useMemo(
        () =>
            options.map(option => ({
                item: option,
            })),
        [options],
    );

    const selectedOption = React.useMemo(
        () => options.find(option => option.value === value),
        [options, value],
    );
    const initialSelected = React.useMemo(() => {
        return value === null || !selectedOption
            ? undefined
            : {
                  item: selectedOption,
              };
    }, [value, selectedOption]);

    const onSelectHandler = React.useCallback(
        (option: TAutocompleteOption<T>) => {
            const newValue = option.value;
            if (newValue === value) return;
            onChange(newValue);
        },
        [onChange, value],
    );

    return (
        <Box
            ref={containerRef}
            data-testid={testId}
            className={clsx([testId, className])}
            sx={{
                width: "100%",
                ...alignInputBorderStyles,
                ...alignDisabledInputStyles(disabled),
                ...sx,
            }}
        >
            <UIKitAutocomplete<TAutocompleteOption<T>>
                textFieldProps={{
                    name: id,
                    label,
                    hideLabel,
                    placeholder,
                    disabled,
                    trailingIcon: disabled ? undefined : "angle-down",
                }}
                initiallySelected={initialSelected}
                allowEmptySelection={false}
                visibleItemsCount={10}
                options={optionsAsAutocompleteOptions}
                onSelect={onSelectHandler}
                getOptionLabel={option => option.label}
                getOptionId={option => option.value}
            />
        </Box>
    );
};

export default Autocomplete;
